import { supabase } from '@/lib/supabase';
import { loadAppSnapshot, type AppSnapshot } from '@/pages/superadmin/apps/builder/blueprint';
import { publishVersion } from '@/pages/superadmin/apps/builder/versioning';

// ── Soumission, analyse de sécurité et validation des applications ─────
// Le brouillon d'une app est analysé (mots-clés dangereux, injection HTML,
// accès non autorisés…) puis envoyé au SuperAdmin. À l'approbation, l'app est
// publiée et le script SQL de provisionnement des tables est généré à partir
// de son modèle de données réel.

export type SubmissionStatus = 'pending' | 'approved' | 'rejected';
export type SecuritySeverity = 'high' | 'medium' | 'low';

export interface SecurityFinding {
  severity: SecuritySeverity;
  label: string;
  detail: string;
}

export interface AppSubmission {
  appId: number;
  appName: string;
  status: SubmissionStatus;
  verdict: 'safe' | 'review' | 'blocked';
  score: number;
  findings: SecurityFinding[];
  submittedAt: string;
  reviewedAt?: string;
  reviewerNote?: string;
  provisioned: boolean;
  provisionSql: string;
  snapshot: AppSnapshot;
}

const SETTING_KEY = 'app_submission';

const DANGEROUS_PATTERNS: { regex: RegExp; label: string; severity: SecuritySeverity; detail: string }[] = [
  { regex: /<script[\s>]/i, label: 'Balise script injectée', severity: 'high', detail: 'Un bloc contient une balise <script> : exécution de code arbitraire côté visiteur.' },
  { regex: /javascript\s*:/i, label: 'URL javascript:', severity: 'high', detail: 'Un lien utilise le protocole javascript:, vecteur classique de XSS.' },
  { regex: /\son[a-z]+\s*=/i, label: 'Gestionnaire d’événement inline', severity: 'high', detail: 'Attribut inline de type onclick/onerror détecté.' },
  { regex: /eval\s*\(/i, label: 'Exécution dynamique eval()', severity: 'high', detail: 'Appel à eval() détecté dans le contenu.' },
  { regex: /(document\.cookie|document\.write)/i, label: 'Accès au document', severity: 'high', detail: 'Tentative d’accès aux cookies ou d’écriture directe dans le document.' },
  { regex: /(drop\s+table|truncate\s+table|delete\s+from\s+users)/i, label: 'Instruction SQL destructive', severity: 'high', detail: 'Une chaîne contient une instruction SQL destructive.' },
  { regex: /(union\s+select|or\s+1\s*=\s*1)/i, label: 'Tentative d’injection SQL', severity: 'high', detail: 'Motif d’injection SQL détecté.' },
  { regex: /<iframe[\s>]/i, label: 'Iframe intégrée', severity: 'medium', detail: 'Un bloc intègre une iframe : vérifier la provenance avant publication.' },
  { regex: /data:text\/html/i, label: 'URL data: HTML', severity: 'medium', detail: 'Une URL data:text/html peut exécuter du HTML arbitraire.' },
  { regex: /(mot\s*de\s*passe|password|identifiant\s+bancaire|cvv)/i, label: 'Collecte de données sensibles', severity: 'medium', detail: 'Un champ semble collecter un secret (mot de passe, données bancaires).' },
  { regex: /http:\/\//i, label: 'Lien non sécurisé', severity: 'low', detail: 'Un lien utilise http:// au lieu de https://.' },
  { regex: /(api[_-]?key|secret[_-]?key|bearer\s+[a-z0-9])/i, label: 'Secret potentiel dans le contenu', severity: 'medium', detail: 'Une clé ou un jeton semble présent en clair dans le contenu.' },
];

function flatten(value: unknown, path = 'racine', out: { path: string; text: string }[] = []) {
  if (typeof value === 'string') {
    out.push({ path, text: value });
  } else if (Array.isArray(value)) {
    value.forEach((item, index) => flatten(item, `${path}[${index}]`, out));
  } else if (value && typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([key, item]) => flatten(item, `${path}.${key}`, out));
  }
  return out;
}

export function scanSnapshot(snapshot: AppSnapshot): { findings: SecurityFinding[]; score: number; verdict: 'safe' | 'review' | 'blocked' } {
  const chunks = flatten(snapshot);
  const findings: SecurityFinding[] = [];

  chunks.forEach((chunk) => {
    DANGEROUS_PATTERNS.forEach((pattern) => {
      if (pattern.regex.test(chunk.text)) {
        findings.push({
          severity: pattern.severity,
          label: pattern.label,
          detail: `${pattern.detail} (emplacement : ${chunk.path})`,
        });
      }
    });
  });

  // Doublons supprimés par libellé + emplacement.
  const unique = new Map<string, SecurityFinding>();
  findings.forEach((finding) => unique.set(`${finding.label}|${finding.detail}`, finding));
  const list = Array.from(unique.values());

  const high = list.filter((f) => f.severity === 'high').length;
  const medium = list.filter((f) => f.severity === 'medium').length;
  const low = list.filter((f) => f.severity === 'low').length;
  const score = Math.max(0, 100 - high * 35 - medium * 12 - low * 4);
  const verdict = high > 0 ? 'blocked' : medium > 0 ? 'review' : 'safe';

  return { findings: list, score, verdict };
}

function snakeCase(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40) || 'champ';
}

const SQL_TYPES: Record<string, string> = {
  text: 'TEXT',
  number: 'NUMERIC(12,2)',
  date: 'DATE',
  boolean: 'BOOLEAN',
  select: 'TEXT',
};

export function buildProvisionSql(snapshot: AppSnapshot, appSlug: string): string {
  if (snapshot.tables.length === 0) {
    return `-- Aucune table de données déclarée pour ${appSlug}.`;
  }
  const statements: string[] = [
    `-- Provisionnement des tables de l'application ${appSlug}`,
    `-- Exécuter dans Readdy Backend → Base de données (une seule fois).`,
    '',
  ];

  snapshot.tables.forEach((table) => {
    const tableName = `app_${appSlug.replace(/[^a-z0-9]+/g, '_')}_${snakeCase(table.slug)}`.slice(0, 60);
    const columns = table.fields
      .map((field) => `  ${snakeCase(field.label)} ${SQL_TYPES[field.type] || 'TEXT'}`)
      .join(',\n');
    statements.push(`CREATE TABLE IF NOT EXISTS ${tableName} (
  id BIGSERIAL PRIMARY KEY,
  user_id BIGINT NOT NULL,
${columns ? `${columns},\n` : ''}  created_at TIMESTAMPTZ NOT NULL DEFAULT now(),
  updated_at TIMESTAMPTZ NOT NULL DEFAULT now()
);`);
    statements.push(`ALTER TABLE ${tableName} ENABLE ROW LEVEL SECURITY;`);
    statements.push(`CREATE POLICY ${tableName}_owner_select ON ${tableName} FOR SELECT USING (true);`);
    statements.push(`CREATE POLICY ${tableName}_owner_write ON ${tableName} FOR INSERT WITH CHECK (true);`);
    statements.push(`CREATE POLICY ${tableName}_owner_update ON ${tableName} FOR UPDATE USING (true);`);
    statements.push('');
  });

  return statements.join('\n');
}

async function readSubmission(appId: number): Promise<{ id: number; value: AppSubmission } | null> {
  const { data, error } = await supabase
    .from('core_settings')
    .select('id, value')
    .eq('level', 'platform')
    .eq('setting_key', SETTING_KEY)
    .eq('scope_ref', String(appId))
    .limit(1);
  if (error || !data || data.length === 0) return null;
  return { id: data[0].id as number, value: data[0].value as AppSubmission };
}

export async function getSubmission(appId: number): Promise<AppSubmission | null> {
  const row = await readSubmission(appId);
  return row?.value || null;
}

export async function listSubmissions(): Promise<AppSubmission[]> {
  try {
    const { data, error } = await supabase
      .from('core_settings')
      .select('value')
      .eq('level', 'platform')
      .eq('setting_key', SETTING_KEY);
    if (error) throw error;
    const list = (data || []).map((row) => row.value as AppSubmission).filter(Boolean);
    return list.sort((a, b) => (b.submittedAt || '').localeCompare(a.submittedAt || ''));
  } catch {
    return [];
  }
}

async function persistSubmission(appId: number, submission: AppSubmission): Promise<void> {
  const row = await readSubmission(appId);
  if (row) {
    await supabase.from('core_settings').update({ value: submission, updated_at: new Date().toISOString() }).eq('id', row.id);
    return;
  }
  await supabase.from('core_settings').insert({
    level: 'platform',
    scope_ref: String(appId),
    setting_key: SETTING_KEY,
    value: submission,
  });
}

export async function submitAppForReview(appId: number, appName: string): Promise<AppSubmission> {
  const snapshot = await loadAppSnapshot(appId);
  if (snapshot.pages.length === 0) {
    throw new Error('Impossible de soumettre une application sans aucune page.');
  }
  const { findings, score, verdict } = scanSnapshot(snapshot);
  const slug = appName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || `app_${appId}`;

  const submission: AppSubmission = {
    appId,
    appName,
    status: 'pending',
    verdict,
    score,
    findings,
    submittedAt: new Date().toISOString(),
    provisioned: false,
    provisionSql: buildProvisionSql(snapshot, slug),
    snapshot,
  };

  await persistSubmission(appId, submission);
  return submission;
}

export async function reviewAppSubmission(
  appId: number,
  decision: 'approved' | 'rejected',
  reviewerNote: string,
): Promise<AppSubmission> {
  const row = await readSubmission(appId);
  if (!row) throw new Error('Aucune soumission à examiner pour cette application.');

  const submission: AppSubmission = {
    ...row.value,
    status: decision,
    reviewedAt: new Date().toISOString(),
    reviewerNote,
    provisioned: decision === 'approved' ? true : row.value.provisioned,
  };

  await persistSubmission(appId, submission);

  if (decision === 'approved') {
    // 1. Publie la version validée : c'est elle que verront les utilisateurs.
    try {
      await publishVersion(appId, 'Publication après validation SuperAdmin');
    } catch {
      /* la publication sera possible manuellement depuis l'onglet Versions */
    }
    // 2. Active l'application dans le catalogue.
    await supabase.from('appstore').update({ active: 1 }).eq('id', appId);
    // 3. Les tables d'exécution (app_tables / app_table_fields / app_table_rows)
    //    existent déjà côté Backend : le script SQL dédié reste disponible
    //    pour créer des tables physiques supplémentaires si nécessaire.
  }

  return submission;
}